import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Avatar,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Home as HomeIcon,
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import {
  getCustomers,
  createCustomer,
  updateCustomer,
  deleteCustomer,
  getCustomerStats,
} from '../services/customerService';
import type {
  Customer,
  CustomerStats,
  CreateCustomerData,
} from '../services/customerService';

const SOURCE_OPTIONS = ['Referral', 'Property listing', 'Website', 'Social media', 'Open house', 'Repeat client', 'Walk-in', 'Other'];

const Customers = () => {
  const { agency, user } = useAuth();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [stats, setStats] = useState<CustomerStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [preferredAreasInput, setPreferredAreasInput] = useState('');
  const [mustHaveFeaturesInput, setMustHaveFeaturesInput] = useState('');
  const [niceToHaveFeaturesInput, setNiceToHaveFeaturesInput] = useState('');
  const [partyType, setPartyType] = useState<'buyer' | 'vendor'>('buyer');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  // Form state
  const [formData, setFormData] = useState<CreateCustomerData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    postcode: '',
    notes: '',
    status: 'prospect',
    source: '',
    tags: [],
    preferredAreas: [],
    propertyTypes: [],
    mustHaveFeatures: [],
    niceToHaveFeatures: [],
    previousProperties: [],
  });

  useEffect(() => {
    // Infer default partyType from route path
    if (location.pathname.includes('/vendors')) {
      setPartyType('vendor');
    } else if (location.pathname.includes('/attendees')) {
      setPartyType('buyer');
    }
  }, [location.pathname]);

  const refreshData = () => setRefreshTrigger((n) => n + 1);

  useEffect(() => {
    if (!agency?.slug) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    const filters: any = {};
    if (searchTerm) filters.search = searchTerm;
    if (statusFilter) filters.status = statusFilter;
    if (partyType) filters.partyType = partyType;

    Promise.all([
      getCustomers(agency.slug, filters),
      getCustomerStats(agency.slug, partyType),
    ])
      .then(([customersData, statsData]) => {
        if (cancelled) return;
        setCustomers(customersData);
        setStats(statsData);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'An error occurred');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [agency?.slug, searchTerm, statusFilter, partyType, refreshTrigger]);

  const handleOpenDialog = (customer?: Customer) => {
    if (customer) {
      setEditingCustomer(customer);
      setFormData({
        firstName: customer.firstName ?? '',
        lastName: customer.lastName ?? '',
        email: customer.email ?? '',
        phone: customer.phone ?? '',
        address: customer.address ?? '',
        city: customer.city ?? '',
        state: customer.state ?? '',
        postcode: customer.postcode ?? '',
        notes: customer.notes ?? '',
        status: customer.status ?? 'prospect',
        source: customer.source ?? '',
        tags: customer.tags ?? [],
        preferredAreas: customer.preferredAreas || [],
        propertyTypes: customer.propertyTypes || [],
        mustHaveFeatures: customer.mustHaveFeatures || [],
        niceToHaveFeatures: customer.niceToHaveFeatures || [],
        previousProperties: customer.previousProperties || [],
      });
      setPreferredAreasInput(customer.preferredAreas?.join(', ') || '');
      setMustHaveFeaturesInput(customer.mustHaveFeatures?.join(', ') || '');
      setNiceToHaveFeaturesInput(customer.niceToHaveFeatures?.join(', ') || '');
    } else {
      setEditingCustomer(null);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        postcode: '',
        notes: '',
        status: 'prospect',
        source: '',
        tags: [],
        preferredAreas: [],
        propertyTypes: [],
        mustHaveFeatures: [],
        niceToHaveFeatures: [],
        previousProperties: [],
      });
      setPreferredAreasInput('');
      setMustHaveFeaturesInput('');
      setNiceToHaveFeaturesInput('');
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCustomer(null);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      postcode: '',
      notes: '',
      status: 'prospect',
      source: '',
      tags: [],
      preferredAreas: [],
      propertyTypes: [],
      mustHaveFeatures: [],
      niceToHaveFeatures: [],
      previousProperties: [],
    });
    setPreferredAreasInput('');
    setMustHaveFeaturesInput('');
    setNiceToHaveFeaturesInput('');
  };

  const handleSave = async () => {
    if (!agency?.slug || !formData.firstName || !formData.lastName || !formData.email) return;

    try {
      setSaving(true);
      if (editingCustomer) {
        await updateCustomer(agency.slug, editingCustomer._id, formData);
        setSuccess('Customer updated successfully');
      } else {
        await createCustomer(agency.slug, formData);
        setSuccess('Customer added successfully');
      }
      handleCloseDialog();
      refreshData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save customer');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (customerId: string) => {
    if (!agency?.slug) return;

    if (!window.confirm('Are you sure you want to delete this customer?')) return;

    try {
      setSaving(true);
      await deleteCustomer(agency.slug, customerId);
      setSuccess('Customer deleted successfully');
      refreshData();
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to delete customer';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'prospect': return 'default';
      case 'active': return 'primary';
      case 'client': return 'success';
      case 'inactive': return 'error';
      default: return 'default';
    }
  };

  if (loading && !customers.length) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography>{partyType === 'vendor' ? 'Loading vendors...' : 'Loading customers...'}</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            {partyType === 'vendor' ? 'Vendors' : 'Attendees'}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            {partyType === 'vendor' ? 'Add Vendor' : 'Add Attendee'}
          </Button>
        </Box>

        {/* Stats Cards */}
        {stats && (
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    {partyType === 'vendor' ? 'Total Vendors' : 'Total Customers'}
                  </Typography>
                  <Typography variant="h4">{stats.total}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    {partyType === 'vendor' ? 'New' : 'Prospects'}
                  </Typography>
                  <Typography variant="h4" color="primary">{stats.prospect}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    {partyType === 'vendor' ? 'Listed' : 'Active'}
                  </Typography>
                  <Typography variant="h4" color="success.main">{stats.active}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    {partyType === 'vendor' ? 'Sold' : 'Clients'}
                  </Typography>
                  <Typography variant="h4" color="info.main">{stats.client}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            {partyType !== 'vendor' && (
              <Grid item xs={12} md={3}>
                <Button
                  component={Link}
                  to={`/agencies/${agency?.slug}/vendors`}
                  variant="outlined"
                  size="medium"
                  sx={{ textTransform: 'none' }}
                >
                  View Vendors →
                </Button>
              </Grid>
            )}
            <Grid item xs={12} md={partyType === 'vendor' ? 9 : 6}>
              <TextField
                fullWidth
                placeholder={partyType === 'vendor' ? 'Search vendors...' : 'Search attendees...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="prospect">{partyType === 'vendor' ? 'New' : 'Prospect'}</MenuItem>
                  <MenuItem value="active">{partyType === 'vendor' ? 'Listed' : 'Active'}</MenuItem>
                  <MenuItem value="client">{partyType === 'vendor' ? 'Sold' : 'Client'}</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {/* Customers Table */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {partyType === 'vendor' ? 'Vendors' : 'Attendees'} ({customers.length})
          </Typography>

          {customers.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{partyType === 'vendor' ? 'Vendor' : 'Attendee'}</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Location</TableCell>
                    {partyType === 'vendor' && <TableCell>Properties</TableCell>}
                    <TableCell>Status</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Assigned To</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {customers.map((customer) => (
                    <TableRow key={customer._id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                            {customer.firstName[0]}{customer.lastName[0]}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2">
                              {customer.firstName} {customer.lastName}
                            </Typography>
                            {customer.tags.length > 0 && (
                              <Box sx={{ mt: 0.5 }}>
                                {customer.tags.slice(0, 2).map((tag, index) => (
                                  <Chip
                                    key={index}
                                    label={tag}
                                    size="small"
                                    sx={{ mr: 0.5, mb: 0.5 }}
                                  />
                                ))}
                                {customer.tags.length > 2 && (
                                  <Chip
                                    label={`+${customer.tags.length - 2}`}
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                            <EmailIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body2">{customer.email}</Typography>
                          </Box>
                          {customer.phone && (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <PhoneIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                              <Typography variant="body2">{customer.phone}</Typography>
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {customer.city && customer.state && (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <LocationIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body2">
                              {customer.city}, {customer.state}
                            </Typography>
                          </Box>
                        )}
                      </TableCell>
                      {partyType === 'vendor' && (
                        <TableCell>
                          {customer.ownedProperties && customer.ownedProperties.length > 0 ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {customer.ownedProperties.slice(0, 3).map((prop: any) => {
                                const addr = prop?.address;
                                const display = addr?.formattedAddress || addr?.street || (addr?.city && addr?.state ? `${addr.city}, ${addr.state}` : addr?.city || addr?.state) || 'Property';
                                const statusLabel = prop?.status ? prop.status.replace('_', ' ') : '';
                                return (
                                  <Box key={prop._id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Link
                                      to={`/agencies/${agency?.slug}/properties/${prop._id}`}
                                      style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', fontSize: '0.875rem', flex: 1, minWidth: 0 }}
                                    >
                                      <HomeIcon sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary', flexShrink: 0 }} />
                                      <Typography variant="body2" noWrap title={display}>{display}</Typography>
                                    </Link>
                                    {statusLabel && (
                                      <Chip label={statusLabel} size="small" sx={{ fontSize: '0.7rem', height: 18 }} />
                                    )}
                                  </Box>
                                );
                              })}
                              {customer.ownedProperties.length > 3 && (
                                <Typography variant="caption" color="text.secondary">
                                  +{customer.ownedProperties.length - 3} more
                                </Typography>
                              )}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        <Chip
                          label={customer.status}
                          color={getStatusColor(customer.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{customer.source || '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        {customer.assignedTo ? (
                          <Typography variant="body2">
                            {customer.assignedTo.firstName} {customer.assignedTo.lastName}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Unassigned
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton onClick={() => handleOpenDialog(customer)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={
                          partyType === 'vendor' && customer.ownedProperties?.length ? 
                            'Cannot delete: vendor is associated with properties' 
                            : 'Delete'
                        }>
                          <span>
                            <IconButton 
                              color="error" 
                              onClick={() => handleDelete(customer._id)}
                              disabled={saving || (partyType === 'vendor' && !!customer.ownedProperties?.length)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <PersonIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography color="text.secondary" variant="h6">
                {partyType === 'vendor' ? 'No vendors found' : 'No attendees found'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {searchTerm || statusFilter 
                  ? (stats && stats.total > 0
                      ? 'No vendors match your current filters. Try clearing the Status filter or search.'
                      : 'Try adjusting your search or filters.')
                  : partyType === 'vendor' 
                    ? 'Vendors are added when you onboard properties with owner contact details. Add a property to get started.'
                    : 'Add your first attendee to get started.'
                }
              </Typography>
              {partyType === 'vendor' && !searchTerm && !statusFilter && agency?.slug && (
                <Button
                  component={Link}
                  to={`/agencies/${agency.slug}/properties/new`}
                  variant="outlined"
                  sx={{ mt: 2 }}
                >
                  Add Property
                </Button>
              )}
            </Box>
          )}
        </Paper>
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingCustomer
            ? (partyType === 'vendor' ? 'Edit Vendor' : 'Edit Customer')
            : (partyType === 'vendor' ? 'Add Vendor' : 'Add New Customer')}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="City"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="State"
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Postcode"
                value={formData.postcode}
                onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
              />
            </Grid>
            {partyType === 'vendor' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Selling Reason"
                  value={formData.sellingReason || ''}
                  onChange={(e) => setFormData({ ...formData, sellingReason: e.target.value })}
                  placeholder="Why are they selling?"
                />
              </Grid>
            )}
            {partyType !== 'vendor' && (
              <>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Budget Min"
                value={formData.budgetMin ? `$${formData.budgetMin.toLocaleString((typeof navigator !== 'undefined' && navigator.language) ? navigator.language : undefined)}` : ''}
                onChange={e => {
                  const value = e.target.value.replace(/[$,]/g, '');
                  const numValue = parseInt(value) || 0;
                  setFormData({ ...formData, budgetMin: numValue });
                }}
                placeholder="$500,000"
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Budget Max"
                value={formData.budgetMax ? `$${formData.budgetMax.toLocaleString((typeof navigator !== 'undefined' && navigator.language) ? navigator.language : undefined)}` : ''}
                onChange={e => {
                  const value = e.target.value.replace(/[$,]/g, '');
                  const numValue = parseInt(value) || 0;
                  setFormData({ ...formData, budgetMax: numValue });
                }}
                placeholder="$800,000"
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Preferred Areas (comma separated)"
                value={preferredAreasInput}
                onChange={e => {
                  setPreferredAreasInput(e.target.value);
                  const areas = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
                  setFormData({ ...formData, preferredAreas: areas });
                }}
                placeholder="e.g., Bondi, Manly, North Sydney"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Property Types</InputLabel>
                <Select
                  multiple
                  value={formData.propertyTypes || []}
                  onChange={e => setFormData({ ...formData, propertyTypes: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value })}
                  label="Property Types"
                  renderValue={(selected) => (selected as string[]).join(', ')}
                >
                  <MenuItem value="House">House</MenuItem>
                  <MenuItem value="Apartment">Apartment</MenuItem>
                  <MenuItem value="Townhouse">Townhouse</MenuItem>
                  <MenuItem value="Land">Land</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Bedrooms"
                type="number"
                value={formData.bedrooms || ''}
                onChange={e => setFormData({ ...formData, bedrooms: Number(e.target.value) })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Bathrooms"
                type="number"
                value={formData.bathrooms || ''}
                onChange={e => setFormData({ ...formData, bathrooms: Number(e.target.value) })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Parking Spaces"
                type="number"
                value={formData.parkingSpaces || ''}
                onChange={e => setFormData({ ...formData, parkingSpaces: Number(e.target.value) })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Must-have Features (comma separated)"
                value={mustHaveFeaturesInput}
                onChange={e => {
                  setMustHaveFeaturesInput(e.target.value);
                  const features = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
                  setFormData({ ...formData, mustHaveFeatures: features });
                }}
                placeholder="e.g., Pool, Garage, Garden"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nice-to-have Features (comma separated)"
                value={niceToHaveFeaturesInput}
                onChange={e => {
                  setNiceToHaveFeaturesInput(e.target.value);
                  const features = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
                  setFormData({ ...formData, niceToHaveFeatures: features });
                }}
                placeholder="e.g., Ocean view, Balcony, Study"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Timeline</InputLabel>
                <Select
                  value={formData.timeline || ''}
                  label="Timeline"
                  onChange={e => setFormData({ ...formData, timeline: e.target.value as any })}
                >
                  <MenuItem value="immediate">Immediate</MenuItem>
                  <MenuItem value="1-3_months">1-3 months</MenuItem>
                  <MenuItem value="3-6_months">3-6 months</MenuItem>
                  <MenuItem value="6-12_months">6-12 months</MenuItem>
                  <MenuItem value="flexible">Flexible</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Urgency</InputLabel>
                <Select
                  value={formData.urgency || ''}
                  label="Urgency"
                  onChange={e => setFormData({ ...formData, urgency: e.target.value as any })}
                >
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Previously Owned Property</InputLabel>
                <Select
                  value={formData.previouslyOwned === undefined ? '' : formData.previouslyOwned ? 'yes' : 'no'}
                  label="Previously Owned Property"
                  onChange={e => setFormData({ ...formData, previouslyOwned: e.target.value === 'yes' })}
                >
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Previous Properties (one per line)"
                multiline
                rows={2}
                value={formData.previousProperties?.join('\n') || ''}
                onChange={e => setFormData({ ...formData, previousProperties: e.target.value.split('\n').map(s => s.trim()).filter(Boolean) })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Selling Reason"
                value={formData.sellingReason || ''}
                onChange={e => setFormData({ ...formData, sellingReason: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Pre-approved</InputLabel>
                <Select
                  value={formData.preApproved === undefined ? '' : formData.preApproved ? 'yes' : 'no'}
                  label="Pre-approved"
                  onChange={e => setFormData({ ...formData, preApproved: e.target.value === 'yes' })}
                >
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Pre-approval Amount"
                value={formData.preApprovalAmount ? `$${formData.preApprovalAmount.toLocaleString((typeof navigator !== 'undefined' && navigator.language) ? navigator.language : undefined)}` : ''}
                onChange={e => {
                  const value = e.target.value.replace(/[$,]/g, '');
                  const numValue = parseInt(value) || 0;
                  setFormData({ ...formData, preApprovalAmount: numValue });
                }}
                placeholder="$750,000"
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Deposit Amount"
                value={formData.depositAmount ? `$${formData.depositAmount.toLocaleString((typeof navigator !== 'undefined' && navigator.language) ? navigator.language : undefined)}` : ''}
                onChange={e => {
                  const value = e.target.value.replace(/[$,]/g, '');
                  const numValue = parseInt(value) || 0;
                  setFormData({ ...formData, depositAmount: numValue });
                }}
                placeholder="$50,000"
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Lender"
                value={formData.lender || ''}
                onChange={e => setFormData({ ...formData, lender: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Family Size"
                type="number"
                value={formData.familySize || ''}
                onChange={e => setFormData({ ...formData, familySize: Number(e.target.value) })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Children</InputLabel>
                <Select
                  value={formData.children === undefined ? '' : formData.children ? 'yes' : 'no'}
                  label="Children"
                  onChange={e => setFormData({ ...formData, children: e.target.value === 'yes' })}
                >
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Pets</InputLabel>
                <Select
                  value={formData.pets === undefined ? '' : formData.pets ? 'yes' : 'no'}
                  label="Pets"
                  onChange={e => setFormData({ ...formData, pets: e.target.value === 'yes' })}
                >
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Work from Home</InputLabel>
                <Select
                  value={formData.workFromHome === undefined ? '' : formData.workFromHome ? 'yes' : 'no'}
                  label="Work from Home"
                  onChange={e => setFormData({ ...formData, workFromHome: e.target.value === 'yes' })}
                >
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            </>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                >
                  <MenuItem value="prospect">{partyType === 'vendor' ? 'New' : 'Prospect'}</MenuItem>
                  <MenuItem value="active">{partyType === 'vendor' ? 'Listed' : 'Active'}</MenuItem>
                  <MenuItem value="client">{partyType === 'vendor' ? 'Sold' : 'Client'}</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="source-label">Source</InputLabel>
                <Select
                  labelId="source-label"
                  label="Source"
                  value={formData.source ?? ''}
                  onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                >
                  <MenuItem value="">Not specified</MenuItem>
                  {SOURCE_OPTIONS.map((opt) => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                  {formData.source && !SOURCE_OPTIONS.includes(formData.source) && (
                    <MenuItem value={formData.source}>{formData.source}</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            disabled={!formData.firstName || !formData.lastName || !formData.email || saving}
          >
            {saving ? 'Saving...' : (editingCustomer ? 'Update' : 'Add')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbars */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <Alert onClose={() => setSuccess(null)} severity="success">
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert onClose={() => setError(null)} severity="error">
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Customers; 